import { useServiceHome } from "@/hooks/serviceHome";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { InputLabel } from "@/screens/update-property/style";
import InputWithIcon from "@/components/input/input";
import { FaWpforms } from "react-icons/fa";
import {
  ErrorMessage,
  Field,
  FieldInputProps,
  FieldProps,
  useFormikContext,
} from "formik";
import { ErrorText, MarginBottom } from "@/screens/login/login-seller/style";
import { IoLocationOutline } from "react-icons/io5";

interface MySpaceLocationSelectorProps {
  onLocationSelected?: (uf: string, city: string) => void;
}

const MySpaceLocationSelector: React.FC<MySpaceLocationSelectorProps> = ({
  onLocationSelected,
}: MySpaceLocationSelectorProps) => {
  const [ufs, setUfs] = useState<
    Array<{ id: number; name: string; uf: string }>
  >([]);
  const [cities, setCities] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedUf, setSelectedUf] = useState("");
  const { getUfs, getCities, getAddressDataByZipCode, getGeocodeFromAddress } =
    useServiceHome();
  const { t } = useTranslation();
  const formik = useFormikContext();

  useEffect(() => {
    const loadUfs = async () => {
      try {
        const response = await getUfs();
        setUfs(response);
        const values = formik.values as any;
        if (values.federal_unity) {
          setSelectedUf(values.federal_unity);
        }
      } catch (error) {
        console.error("Error loading UFs:", error);
      }
    };
    loadUfs();
  }, []);

  useEffect(() => {
    const loadCities = async () => {
      if (selectedUf) {
        try {
          const response = await getCities(selectedUf);
          setCities(response);
        } catch (error) {
          console.error("Error loading cities:", error);
        }
      } else {
        setCities([]);
      }
    };
    loadCities();
  }, [selectedUf]);

  const handleUfChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const uf = e.target.value;
    setSelectedUf(uf);
    if (onLocationSelected) {
      onLocationSelected(uf, "");
    }
  };

  const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const city = e.target.value;
    if (onLocationSelected) {
      onLocationSelected(selectedUf, city);
    }
  };

  return (
    <>
{/*      <InputLabel>
        {t("address")}
        <span className="text-red-600">*</span>
      </InputLabel>
      <Field
        name="address"
        validate={(value: string) => {
          if (!value || value.length === 0) {
            return t("required");
          }
        }}
      >
        {({ field, meta }: FieldProps) => (
          <InputWithIcon
            icon={<FaWpforms />}
            iconSize="23px"
            field={field}
            placeholder={t("type-address")}
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: "5px",
              border: `1px solid ${
                meta.touched && meta.error ? "red" : "black"
              }`,
            }}
          />
        )}
      </Field>
      <ErrorText>
        <ErrorMessage name="address" />
      </ErrorText>*/}

      <div>
        <InputLabel>{t("zip_code")}</InputLabel>
        <Field
          type="text"
          name="zip_code"
          render={({ field }: { field?: FieldInputProps<any> }) => (
            <InputWithIcon
              icon={<IoLocationOutline />}
              iconSize="23px"
              field={field}
              placeholder={t("type-zip-code")}
              onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                if (field) {
                  field.onChange(e);
                  const zipCode = e.target.value.replace(/\D/g, "");
                  if (zipCode.length === 8) {
                    try {
                      const addressData = await getAddressDataByZipCode(
                        zipCode
                      );
                      if (addressData) {
                        // Find UF in ufs array
                        const ufMatch = ufs.find(
                          (uf) => uf.uf === addressData.estado
                        );
                        if (ufMatch) {
                          setSelectedUf(ufMatch.uf);
                          // Use Formik context to update fields
                          formik.setFieldValue(
                            "street",
                            addressData.logradouro
                          );
                          formik.setFieldValue(
                            "complement",
                            addressData.complemento
                          );
                          formik.setFieldValue(
                            "neighborhood",
                            addressData.bairro
                          );
                          formik.setFieldValue(
                            "federal_unity",
                            addressData.estado
                          );
                          formik.setFieldValue("city", addressData.cidade);
                        }
                      }
                    } catch (error) {
                      console.error("Error fetching address:", error);
                    }
                  }
                }
              }}
            />
          )}
        />
        <ErrorText>
          <ErrorMessage name="zip_code" />
        </ErrorText>

        <MarginBottom />

        <InputLabel>{t("street")}</InputLabel>
        <Field
          type="text"
          name="street"
          render={({ field }: { field?: FieldInputProps<any> }) => (
            <InputWithIcon
              icon={<IoLocationOutline />}
              iconSize="23px"
              field={field}
              placeholder={t("type-street")}
            />
          )}
        />
        <ErrorText>
          <ErrorMessage name="street" />
        </ErrorText>

        <MarginBottom />

        <InputLabel>{t("house_number")}</InputLabel>
        <Field
          type="text"
          name="house_number"
          render={({ field }: { field?: FieldInputProps<any> }) => (
            <InputWithIcon
              icon={<IoLocationOutline />}
              iconSize="23px"
              field={field}
              placeholder={t("type-house-number")}
            />
          )}
        />
        <ErrorText>
          <ErrorMessage name="house_number" />
        </ErrorText>

        <MarginBottom />

        <InputLabel>{t("neighborhood")}</InputLabel>
        <Field
          type="text"
          name="neighborhood"
          render={({ field }: { field?: FieldInputProps<any> }) => (
            <InputWithIcon
              icon={<IoLocationOutline />}
              iconSize="23px"
              field={field}
              placeholder={t("type-neighborhood")}
            />
          )}
        />
        <ErrorText>
          <ErrorMessage name="neighborhood" />
        </ErrorText>
      </div>

      <MarginBottom />
      <div className="flex items-center gap-2">
        <div className="flex flex-col items-start w-1/3">
          <InputLabel>
            {t("federal_unity")}
            <span className="text-red-600">*</span>
          </InputLabel>
          <Field
            name="federal_unity"
            validate={(value: string) => {
              let errorMessage;
              if (!value || value.length == 0) {
                return (errorMessage = t("required"));
              }
            }}
          >
            {({ field, form, meta }: FieldProps) => (
              <div className="w-full">
                <select
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    handleUfChange(e);
                  }}
                  style={{
                    width: "100%",
                    padding: "10px",
                    borderRadius: "5px",
                    border: `1px solid ${
                      meta.touched && meta.error ? "red" : "#ccc"
                    }`,
                    marginBottom: `${
                      meta.touched && meta.error ? "0px" : "15px"
                    }`,
                  }}
                >
                  <option value="">{t("select-state")}</option>
                  {ufs.map((uf) => (
                    <option key={uf.id} value={uf.uf}>
                      {uf.name}
                    </option>
                  ))}
                </select>
                {meta.touched && meta.error && (
                  <div
                    className="error"
                    style={{
                      color: "red",
                      fontSize: "14px",
                      marginBottom: "15px",
                    }}
                  >
                    {meta.error}
                  </div>
                )}
              </div>
            )}
          </Field>
        </div>
        {/* <ErrorText>
              <ErrorMessage name="federal_unity" />
          </ErrorText> */}

        {/* <MarginBottom /> */}

        <div className="flex flex-col items-start w-1/2">
          <InputLabel>
            {t("city")}
            <span className="text-red-600">*</span>
          </InputLabel>
          <Field
            name="city"
            validate={(value: string) => {
              let errorMessage;
              if (!value || value.length == 0) {
                return (errorMessage = t("required"));
              }
            }}
          >
            {({ field, form, meta }: FieldProps) => (
              <div className="w-full">
                <select
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    handleCityChange(e);
                  }}
                  style={{
                    width: "100%",
                    padding: "10px",
                    borderRadius: "5px",
                    border: `1px solid ${
                      meta.touched && meta.error ? "red" : "#ccc"
                    }`,
                    marginBottom: `${
                      meta.touched && meta.error ? "0px" : "15px"
                    }`,
                  }}
                  disabled={!selectedUf}
                >
                  <option value="">{t("select-city")}</option>
                  {cities.map((city) => (
                    <option key={city.id} value={city.name}>
                      {city.name}
                    </option>
                  ))}
                </select>
                {meta.touched && meta.error && (
                  <div
                    className="error"
                    style={{
                      color: "red",
                      fontSize: "14px",
                      marginBottom: "15px",
                    }}
                  >
                    {meta.error}
                  </div>
                )}
              </div>
            )}
          </Field>
        </div>
      </div>
    </>
  );
};

export default MySpaceLocationSelector;
